import type { ArticleV2, ImageGridModule } from 'types/capi/article';
import type { Cta, InternalLink } from 'types/capi/shared';

import ExternalIcon from '~/assets/icons/ExternalIcon';
import AffiliatePageLink from '~/components/AffiliatePageLink';
import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import { setPageContext } from '~/utils/pageContext';

type Props = {
  articleType: ArticleV2['article_type'];
  items: ImageGridModule['items'];
};

export default function ImageGrid({ articleType, items }: Props) {
  const imageCount = Math.min(items.length, 2);

  const portraitImageWidth =
    'max-w-none sm:max-w-[340px] md:max-w-[456px] lg:max-w-[552px]';

  const getImageContainerWidth = () => {
    if (imageCount === 2) return 'w-full';
    if (items[0]?.ratio === '4:5') return portraitImageWidth;

    if (items[0]?.ratio === null) {
      if (articleType === 'news') return 'w-full';
      else if (articleType === 'feature') return portraitImageWidth;
    }

    return 'w-full';
  };

  return (
    <div className="flex w-full items-center justify-center px-4 md:px-10">
      <div
        className={`grid max-w-6xl gap-4 md:gap-6 ${
          imageCount === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'
        } ${getImageContainerWidth()}`}
      >
        {items.map((image, index) => (
          <div key={image.url} className="relative flex flex-col">
            <div className="relative">
              <Image
                alt={image.alt || `image-${index}`}
                src={image.url}
                progressiveLoadSettings={{
                  highResQuality: 100,
                  lowResQuality: 50
                }}
                className="w-full"
                loading="eager"
              />

              {image.cta && (
                <div className="absolute bottom-3 right-3">
                  <div className="w-auto max-w-40 flex-row truncate rounded-full bg-gray-800/70 px-3 py-1.5 text-sm text-grey-tile md:max-w-56">
                    {image.cta.type === 'internal' ? (
                      <InternalCta cta={image.cta} />
                    ) : (
                      <AffiliateCta cta={image.cta} />
                    )}
                  </div>
                </div>
              )}
            </div>
            {image.caption && (
              <p className="mt-2 text-sm text-grey-hide">{image.caption}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function InternalCta({ cta }: { cta: InternalLink }) {
  return (
    <Tracked.InternalLink
      to={cta.path}
      name={`FEATURES_${cta.text}`}
      pagePosition="Image Grid"
      variant="none"
      className="hover:underline"
      onClick={() => setPageContext({ surface_source: 'article' })}
    >
      <div className="flex items-center gap-2">
        <span className="truncate">{cta.text}</span>
      </div>
    </Tracked.InternalLink>
  );
}

function AffiliateCta({ cta }: { cta: Cta }) {
  return (
    <AffiliatePageLink
      urlTemplates={cta}
      buttonName={`FEATURES_${cta.text}`}
      pagePosition="Image Grid"
      variant="none"
      className="hover:underline"
      affiliate={cta.affiliate}
    >
      <div className="flex items-center gap-2">
        <div>
          <ExternalIcon color="#FAFAFA" size={20} />
        </div>
        <span className="truncate">{cta.text}</span>
      </div>
    </AffiliatePageLink>
  );
}
